import React, { useCallback, useEffect, useMemo, useState } from 'react';
import mjml from 'mjml-browser';
import {
	getPageIdx,
	IPage,
	JsonToMjml,
	resolveMergeTagDisplayValue as resolveMergeTagDisplayValueCore,
	subscribeListings,
} from '@bay-email/core';
import { cloneDeep, isEqual } from 'lodash';
import { useEditorContext } from '@/hooks/useEditorContext';
import { HtmlStringToReactNodes } from '@/utils/HtmlStringToReactNodes';
import { createPortal } from 'react-dom';
import { useEditorProps } from '@/hooks/useEditorProps';
import { getEditorRoot, getShadowRoot } from '@/utils';
import { getIsApplyingFormat, subscribeFormatRelease } from '@/utils/formatInteractionGuard';
import { shouldCommitContentEditable } from '@/utils/contenteditable';
import {
	DATA_RENDER_COUNT,
	FIXED_CONTAINER_ID,
	RICH_TEXT_BAR_ID,
	DATA_CONTENT_EDITABLE_IDX,
	DATA_CONTENT_EDITABLE_TYPE,
	ContentEditableType,
} from '@/constants';
import { stripMjmlDefaultHeadReset } from '@/utils/stripMjmlDefaultHeadReset';
import { MergeTagBadge } from '@/utils/MergeTagBadge';

let count = 0;

function extractExternalStylesheetHrefs(html: string) {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return [...doc.head.querySelectorAll('link[rel="stylesheet"]')]
		.map(node => node.getAttribute('href') || '')
		.filter(Boolean);
}

export function MjmlDomRender() {
	const { pageData: content, formHelpers } = useEditorContext();
	const [pageData, setPageData] = useState<IPage | null>(() => cloneDeep(content));
	const [ref, setRef] = useState<HTMLDivElement | null>(null);
	const { dashed, mergeTags, enabledMergeTagsBadge, mergeTagGenerate, realUserProfile, isDesignAdmin } = useEditorProps();

	// A badge chip should show the current viewer's real value, not the sample
	// data sitting at the same key in mergeTags.user — realUserProfile carries
	// ONLY real fields (see PropsProvider's doc comment), so an unresolved key
	// here correctly falls through to the badge's normal label display rather
	// than leaking sample/mock data into a real user's canvas.
	// Resolution itself lives in @bay-email/core so it is unit-testable - this
	// logic shipped wrong twice while it sat inline in a component with no way to
	// assert it. Keep this wrapper thin.
	const resolveMergeTagDisplayValue = useCallback(
		(tagKey: string, blockIdx?: string | null) =>
			resolveMergeTagDisplayValueCore({
				tagKey,
				blockIdx,
				content: content as IPage,
				realUserProfile,
				isDesignAdmin,
			}),
		[realUserProfile, content, isDesignAdmin],
	);
	const [isTextFocus, setIsTextFocus] = useState(false);
	// Bumped when the format guard releases so the freeze effect below re-runs and
	// applies model changes (line height, letter spacing) it skipped while held.
	const [formatRelease, setFormatRelease] = useState(0);
	useEffect(() => subscribeFormatRelease(() => setFormatRelease(n => n + 1)), []);

	const isTextFocusing =
		document.activeElement === getEditorRoot() &&
		getShadowRoot().activeElement?.getAttribute('contenteditable') === 'true';

	useEffect(() => {
		// Freeze the rendered model ONLY while actively editing text or applying a
		// format (colour picker / toolbar). This keeps the contenteditable from being
		// rebuilt mid-format (which would lose the selection). Block operations —
		// drag/move, duplicate, delete, merge-tag insert — do NOT hold the format guard
		// and do NOT keep the contenteditable focused, so they unfreeze and re-render
		// immediately.
		if (isTextFocus || getIsApplyingFormat()) return;
		if (!isEqual(content, pageData)) {
			setPageData(cloneDeep(content));
		}
	}, [content, pageData, setPageData, isTextFocus, formatRelease]);

	useEffect(() => {
		// Non-sticky: true only while a contenteditable is actually focused. When focus
		// leaves (toolbar/picker/block-op) this goes false, so pending model changes can
		// re-render. Selection during formatting is protected by getIsApplyingFormat()
		// in the freeze effect above, not by pinning this flag on.
		setIsTextFocus(isTextFocusing);
	}, [isTextFocusing]);

	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (getEditorRoot()?.contains(e.target as Node)) {
				return;
			}
			const fixedContainer = document.getElementById(FIXED_CONTAINER_ID);
			if (fixedContainer?.contains(e.target as Node)) {
				return;
			}
			// The floating toolbar is portaled to document.body, outside both roots.
			if (document.getElementById(RICH_TEXT_BAR_ID)?.contains(e.target as Node)) {
				return;
			}
			setIsTextFocus(false);
		};

		window.addEventListener('click', onClick);
		return () => {
			window.removeEventListener('click', onClick);
		};
	}, []);

	useEffect(() => {
		const root = getShadowRoot();
		if (!root) return;
		const onClick = () => {
			const isFocusing =
				getShadowRoot().activeElement?.getAttribute('contenteditable') === 'true';
			if (isFocusing) {
				setIsTextFocus(true);
			}
		};

		// What the node held when the user entered it. Blocks render with their
		// merge tags already resolved — an attached listing's address replaces
		// `{{listing.address}}` before the node is editable — so comparing the DOM
		// against the STORED template always differs, and a focus/blur with no
		// typing used to persist the resolved text over the template. The tag was
		// then gone for good: changing the listing had nothing left to re-resolve.
		const contentOnFocus = new WeakMap<Element, string>();

		const readContent = (node: Element, contentEditableType: string | null) =>
			contentEditableType === ContentEditableType.RichText
				? node.innerHTML || ''
				: node.textContent?.trim() || '';

		const onFocus = (e: Event) => {
			if (e.target instanceof Element && e.target.getAttribute('contenteditable')) {
				contentOnFocus.set(
					e.target,
					readContent(e.target, e.target.getAttribute(DATA_CONTENT_EDITABLE_TYPE)),
				);
			}
		};

		const onBlur = (e: Event) => {
			// TiptapEditable commits its own content.
			if (e.target instanceof HTMLElement && e.target.dataset.editor === 'tiptap') return;
			// Save contenteditable changes to form when user blurs
			if (e.target instanceof Element && e.target.getAttribute('contenteditable')) {
				const targetIdx = e.target.getAttribute(DATA_CONTENT_EDITABLE_IDX);
				const contentEditableType = e.target.getAttribute(DATA_CONTENT_EDITABLE_TYPE);

				if (targetIdx) {
					const rawContent = readContent(e.target, contentEditableType);

					const snapshot = contentOnFocus.get(e.target);
					contentOnFocus.delete(e.target);
					if (!shouldCommitContentEditable(snapshot, rawContent)) return;

					const newContent =
						enabledMergeTagsBadge && contentEditableType === ContentEditableType.RichText
							? MergeTagBadge.revert(rawContent, mergeTagGenerate)
							: rawContent;

					// Only save if content actually changed
					const currentValue = formHelpers.getFieldState(targetIdx)?.value;
					if (currentValue !== newContent) {
						formHelpers.change(targetIdx, newContent);
						formHelpers.blur(targetIdx);
					}
				}
			}
		};

		root.addEventListener('click', onClick);
		root.addEventListener('focus', onFocus, true);
		root.addEventListener('blur', onBlur, true);
		return () => {
			root.removeEventListener('click', onClick);
			root.removeEventListener('focus', onFocus, true);
			root.removeEventListener('blur', onBlur, true);
		};
	}, [enabledMergeTagsBadge, formHelpers, mergeTagGenerate]);

	// Listing blocks resolve their bindings during render through a cache that
	// fills asynchronously. Bumping this on every resolution re-runs the memo so
	// the canvas swaps its loading state for real data.
	const [listingRevision, setListingRevision] = useState(0);
	useEffect(
		() => subscribeListings(() => setListingRevision(value => value + 1)),
		[],
	);

	const html = useMemo(() => {
		if (!pageData) return '';

		const dataSource = cloneDeep(mergeTags) || {};
		// dataSource.user is mergeTags.user — real overrides merged over hardcoded
		// sample data, so a real field left blank still reads the sample value at
		// that key. realUserProfile carries ONLY real fields, so a block that needs
		// to tell "the real value is blank" apart from "no real user context" reads
		// this instead of dataSource.user.
		dataSource.realUserProfile = realUserProfile;

		const renderHtml = mjml(
			JsonToMjml({
				data: pageData,
				idx: getPageIdx(),
				context: pageData,
				mode: 'testing',
				dataSource,
			}),
		).html;
		return stripMjmlDefaultHeadReset(renderHtml);
	}, [mergeTags, pageData, listingRevision, realUserProfile]);

	useEffect(() => {
		if (!html) return;

		const hrefs = extractExternalStylesheetHrefs(html);
		if (hrefs.length === 0) return;

		const injectedLinks: HTMLLinkElement[] = [];

		hrefs.forEach(href => {
			const existingLink = document.head.querySelector(
				`link[data-bay-email-preview-font="true"][href="${CSS.escape(href)}"]`,
			);
			if (existingLink) return;

			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = href;
			link.setAttribute('data-bay-email-preview-font', 'true');
			document.head.appendChild(link);
			injectedLinks.push(link);
		});

		return () => {
			injectedLinks.forEach(link => link.remove());
		};
	}, [html]);

	return useMemo(() => {
		return (
			<div
				{...{
					[DATA_RENDER_COUNT]: count++,
				}}
				data-dashed={dashed}
				ref={setRef}
				style={{
					outline: 'none',
					position: 'relative',
				}}
				role='tabpanel'
				tabIndex={0}
			>
				<>
					{ref &&
						createPortal(
							HtmlStringToReactNodes(html, {
								enabledMergeTagsBadge: Boolean(enabledMergeTagsBadge),
								resolveMergeTagDisplayValue,
							}),
							ref,
						)}
				</>
			</div>
		);
	}, [dashed, ref, html, enabledMergeTagsBadge, resolveMergeTagDisplayValue]);
}
